import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <div className="border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">L</span>
            </div>
            <span className="text-xl font-bold text-white">
              Loca<span className="text-emerald-400">Leap</span>
            </span>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-10 h-10 text-emerald-400" />
          <h1 className="text-4xl font-bold text-white">Privacy Policy</h1>
        </div>

        <p className="text-slate-400 mb-8">
          Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>

        <div className="prose prose-invert max-w-none">
          <div className="space-y-8 text-slate-300">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Information We Collect</h2>
              <p className="leading-relaxed mb-4">
                When you use LocaLeap, we collect information you provide directly to us, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-400">
                <li>Contact information (name, email address, phone number)</li>
                <li>Business information (company name, industry, location, goals)</li>
                <li>Competitor information you provide for analysis</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. How We Use Your Information</h2>
              <p className="leading-relaxed mb-4">We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-2 text-slate-400">
                <li>Generate your personalized brand strategy report</li>
                <li>Send you your report via email</li>
                <li>Send follow-up communications related to your report</li>
                <li>Improve our services and develop new features</li>
                <li>Respond to your requests and provide customer support</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Information Sharing</h2>
              <p className="leading-relaxed">
                We do not sell, trade, or rent your personal information to third parties. We may share your information with:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-400 mt-4">
                <li>Service providers who assist in operating our platform (e.g., email delivery services)</li>
                <li>Professional advisors and consultants at VSW Agency / Merge Publishing when you request consultation services</li>
                <li>Law enforcement when required by law</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Data Security</h2>
              <p className="leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Data Retention</h2>
              <p className="leading-relaxed">
                We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this policy, unless a longer retention period is required by law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Your Rights</h2>
              <p className="leading-relaxed mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 text-slate-400">
                <li>Access and receive a copy of your personal data</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Unsubscribe from marketing communications at any time</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Third-Party Services</h2>
              <p className="leading-relaxed">
                Our service uses AI technology to generate brand strategies. The AI-generated content is based on the information you provide and publicly available data. We use third-party services including Anthropic (AI), SerpAPI (business data), and Resend (email delivery).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Cookies</h2>
              <p className="leading-relaxed">
                We use essential cookies to ensure the proper functioning of our website. We do not use tracking cookies for advertising purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Contact Us</h2>
              <p className="leading-relaxed">
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-slate-900/50 border border-slate-800 rounded-xl">
                <p className="text-emerald-400 font-semibold">VSW Agency / Merge Publishing</p>
                <p className="text-slate-400">Email: <a href="mailto:don@vswagency.com" className="text-emerald-400 hover:underline">don@vswagency.com</a></p>
                <p className="text-slate-400">Website: <a href="http://vswagency.com" className="text-emerald-400 hover:underline" target="_blank" rel="noopener noreferrer">vswagency.com</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Simple Footer */}
      <div className="border-t border-slate-800 py-8 px-4">
        <div className="max-w-4xl mx-auto text-center text-slate-500 text-sm">
          © {new Date().getFullYear()} LocaLeap by VSW Agency / Merge Publishing. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
